import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import PrimaryButton from '../PrimaryButton';
import ConfirmPopup from '../ConfirmPopup';
import AnnouncePopup from '../AnnouncePopup';
import PopupMenu from '../PopupMenu';
import { getImageName, triggerEvent } from '../../utils';
import AnnounceIcon from '../../../public/assets/images/icons/megaphone.svg';
import DeleteIcon from '../../icons/white-trash.svg';
import CopyIcon from '../../../public/assets/images/icons/copy-to-clipboard.svg';

import {
  LoopFooterSection,
  BottomPanel,
  DeleteButton,
  AnnounceButton,
  LockButton,
  CopyButton,
  Wrapper,
} from './styles';

const FooterSection = ({
  editor,
  panel,
  flash,
  data,
  payload,
  setPayload,
  save,
  translations,
  showSaveButton,
  isSession,
}) => {
  const { options, useAutoSave } = editor;
  const { userToken, circle_id } = options;
  const hubId = data.uuid;
  const getLockState = () => (data.lock_state === 'code' ? 'password' : data.lock_state);
  const isStartHub = hubId === editor.startHub.id();

  const [confirmPopupIsOpen, setConfirmPopupIsOpen] = useState(false);
  const [announcePopupIsOpen, setAnnouncePopupIsOpen] = useState(false);
  const [showLockSelector, setShowLockSelector] = useState(false);
  const [lock, setLock] = useState(getLockState());
  const [code, setCode] = useState(data.code || '');

  useEffect(() => {
    if (getLockState() !== lock) {
      const lock_state = lock === 'password' ? 'code' : lock;
      setPayload({ ...payload, lock_state, code: '', immediateSave: true });
      if (useAutoSave) {
        triggerEvent('lock.updated', { uuid: hubId, lock_state, code: '' });
      }
    }
  }, [lock]);

  useEffect(() => {
    if (data.code !== code && lock === 'password') {
      setPayload({ ...payload, lock_state: 'code', code, immediateSave: true });
      if (useAutoSave) {
        triggerEvent('lock.updated', { uuid: hubId, lock_state: 'code', code });
      }
    }
  }, [code]);

  const toggleAnnouncePopup = () => {
    setAnnouncePopupIsOpen(!announcePopupIsOpen);
  };

  const deleteHub = () => {
    setConfirmPopupIsOpen(!confirmPopupIsOpen);
    triggerEvent('deleteNode', { nodeId: hubId });
    panel.hide();
  };

  const copyHub = () => {
    triggerEvent('copyToClipboard', {
      uuid: hubId,
      name: data.name,
      imageName: getImageName(data.image),
      type: '',
      copiedAt: Date.now(),
    });
  };

  const selectLock = () => {
    setShowLockSelector(!showLockSelector);
  };

  const appTexts = translations.app;
  const lockTexts = translations.node.lock_states;
  const lockText = translations.node.attributes.lock_state;

  const getLockTypeIcon = (lockType) => `/assets/images/theme-2/icons/toolbar/${lockType}.svg`;

  const menuOptions = ['unlocked', 'locked', 'password'].map((lockType) => ({
    name: lockTexts[lockType],
    src: getLockTypeIcon(lockType),
    tag: lockType,
    selected: lock === lockType,
    value: lockType === 'password' ? code : '',
    onConfirm: (password) => {
      setCode(password);
      setShowLockSelector(false);
    },
    onSelect: (selectedLock) => {
      setLock(selectedLock);
      if (selectedLock !== 'password') {
        setCode('');
        setShowLockSelector(false);
      }
    },
  }));

  return (
    <LoopFooterSection>
      <BottomPanel>
        <Wrapper>
          {!isStartHub && (
            <DeleteButton src={DeleteIcon} title={appTexts.delete} onClick={() => setConfirmPopupIsOpen(true)}>
              {appTexts.delete}
            </DeleteButton>
          )}
          <LockButton id="lockTypes" src={getLockTypeIcon(lock)} title={lockText} onClick={selectLock}>
            {lockTexts[lock]}
          </LockButton>
          <CopyButton src={CopyIcon} title={appTexts.copy_to_clipboard} onClick={copyHub}>
            {appTexts.copy}
          </CopyButton>
          {isSession && (
            <AnnounceButton
              src={AnnounceIcon}
              title={i18n.__('app_announce_explaination')}
              onClick={toggleAnnouncePopup}
            >
              {i18n.__('app_announce')}
            </AnnounceButton>
          )}
        </Wrapper>
        {showSaveButton && (
          <Wrapper>
            <PrimaryButton onClick={save} text={i18n.__('app_save')} className="save-button" />
          </Wrapper>
        )}
      </BottomPanel>
      <PopupMenu
        id="lockTypes"
        popupIsOpen={showLockSelector}
        onClose={() => setShowLockSelector(false)}
        options={menuOptions}
      />
      <ConfirmPopup
        popupIsOpen={confirmPopupIsOpen}
        id="confirmDeleteHub"
        title={`${i18n.__('delete_hub')} '${data.name}'`}
        message={i18n.__('delete_confirm')}
        onCancel={() => setConfirmPopupIsOpen(false)}
        onConfirm={deleteHub}
      />
      <AnnouncePopup
        userToken={userToken}
        loopId={circle_id}
        nodeId={hubId}
        subType={'hub'}
        popupIsOpen={announcePopupIsOpen}
        onClose={() => setAnnouncePopupIsOpen(false)}
        flash={flash}
      />
    </LoopFooterSection>
  );
};

export default FooterSection;

FooterSection.propTypes = {
  editor: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  translations: PropTypes.object.isRequired,
  showSaveButton: PropTypes.bool.isRequired,
  isSession: PropTypes.bool.isRequired,
};
